"use client";

import { agentsFiltersAtom } from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api-client";
import type { IndustryCategory } from "@/lib/types";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { Fragment, type PropsWithChildren, useEffect, useState } from "react";

type CheckboxTileProps = PropsWithChildren<{
  id?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}>;

function CheckboxTile({
  id,
  checked,
  onCheckedChange,
  children,
}: CheckboxTileProps) {
  return (
    <Label
      className="h-16 px-6 flex items-center gap-6 cursor-pointer"
      htmlFor={id}
    >
      <div className="text-base flex-1 font-medium">{children}</div>

      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="w-5 h-5 border-2 rounded-[6px]"
      />
    </Label>
  );
}

export function AgentIndustryFilter() {
  const t = useTranslations();

  const [filters, setFilters] = useAtom(agentsFiltersAtom);
  const [searchValue, setSearchValue] = useState<string>("");

  const { data } = api.listings.getIndustryCategories.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const [filteredIndustries, setFilteredIndustries] = useState<
    IndustryCategory[]
  >([]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const term = searchValue
      .toLowerCase()
      .trim()
      .replace(/[^a-z]/g, "");

    if (!term) {
      setFilteredIndustries(data.industryCategories);
      return;
    }

    const newFilteredIndustries = data.industryCategories.filter((category) =>
      category.name
        .toLowerCase()
        .replace(/[^a-z]/g, "")
        .includes(term),
    );

    setFilteredIndustries(newFilteredIndustries);
  }, [data, searchValue]);

  function onIndustryCategoryCheckedChange(
    category: IndustryCategory,
    value: boolean,
  ) {
    if (value) {
      setFilters((prev) => ({
        ...prev,
        specialtyIds: [...prev.specialtyIds, category.id],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        specialtyIds: prev.specialtyIds.filter((id) => id !== category.id),
      }));
    }
  }

  function onWantsToRemoveIndustryCategory(id: number) {
    setFilters((prev) => ({
      ...prev,
      specialtyIds: prev.specialtyIds.filter((typeId) => typeId !== id),
    }));
  }

  return (
    <>
      <div className="sticky bg-background">
        <div className="p-2 flex flex-col gap-2">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t(
              "saas.search.filters.fields.businessTypes.search.placeholder",
            )}
            className="h-12"
          />

          {data && filters.specialtyIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.industryCategories
                .filter((type) => filters.specialtyIds.includes(type.id))
                .map((type) => (
                  <Button key={type.id} variant="outline" asChild>
                    <div>
                      <span className="overflow-hidden text-ellipsis">
                        {type.name}
                      </span>

                      <div
                        className="p-1 cursor-pointer"
                        onClick={() => onWantsToRemoveIndustryCategory(type.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            onWantsToRemoveIndustryCategory(type.id);
                          }
                        }}
                      >
                        <Icon icon="XClose" />
                      </div>
                    </div>
                  </Button>
                ))}
            </div>
          )}
        </div>

        <Separator />
      </div>

      <div className="max-h-full md:max-h-[600px] overflow-y-auto">
        <div className="flex flex-col">
          {filteredIndustries.map((industry, idx) => (
            <Fragment key={industry.id}>
              {idx > 0 && <Separator />}

              <CheckboxTile
                id={`industry-${industry.id}`}
                checked={filters.specialtyIds.includes(industry.id)}
                onCheckedChange={(value) =>
                  onIndustryCategoryCheckedChange(industry, value)
                }
              >
                {industry.name}
              </CheckboxTile>

              {idx < filteredIndustries.length - 1 && <Separator />}
            </Fragment>
          ))}
        </div>
      </div>
    </>
  );
}

AgentIndustryFilter.displayName = "AgentIndustryFilter";
